import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { getApiUrl, getAuthKey } from "../utils/apiUtils";
import { hasActiveSubscription } from "../utils/globalVariables";
import { useCurrentUser } from "../utils/useCurrentUser";
import { useStoreState } from "easy-peasy";
import khayatLogo from "../../src/assets/images/logo.png";
import Cookies from "js-cookie";
import { clearLocalStorage } from "../utils/functions";
import Modal from "./Modal";
import {
  AlertTriangle,
  Globe,
  Home,
  Menu,
  PowerIcon,
  Settings,
  Sparkles,
  User,
  X,
} from "lucide-react";

const Navbar = () => {
  const [menu, setMenu] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const profileMenuRef = useRef(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [person, setPerson] = useState({
    userIconPath: "",
    userPhotoPath: "",
  });
  const user = useStoreState((state) => state.user);
  const { fullProfile } = useCurrentUser(true);
  const apiUrl = getApiUrl("/users/language");
  const authKey = getAuthKey();

  const handleLogout = async () => {
    setShowLogoutConfirm(false);
    Cookies.remove("jwt");
    clearLocalStorage();
    navigate("/users/login");
    toast.success("Successfully logged out");
  };

  const updateUserLanguage = async (language) => {
    try {
      i18n.changeLanguage(language);
      document.body.dir = i18n.dir();

      await axios.post(
        apiUrl,
        { language: language },
        {
          headers: { Authorization: `Bearer ${authKey}` },
        }
      );
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const handleLanguageChange = (e) => {
    const language = e.target.value;
    updateUserLanguage(language);
  };

  useEffect(() => {
    const userIconPath = localStorage.getItem("userIconPath");
    const userPhotoPath = localStorage.getItem("userPhotoPath");

    if (userIconPath || userPhotoPath) {
      setPerson({
        userIconPath: userIconPath || "",
        userPhotoPath: userPhotoPath || "",
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderIcon = (user) => {
    if (user.userIconPath && user.userIconPath !== "null") {
      return (
        <img
          src={user.userIconPath}
          alt="logo"
          className="w-[4rem] h-[4rem] rounded-full object-cover"
        />
      );
    } else {
      return (
        <img src={khayatLogo} alt="logo" className="max-w-[14rem] h-[4rem]" />
      );
    }
  };

  const renderUserPhoto = (user) => {
    if (user.userPhotoPath && user.userPhotoPath !== "null") {
      return (
        <img
          src={user.userPhotoPath}
          alt="User"
          className="w-[2.75rem] h-[2.75rem] rounded-full object-cover ring-2 ring-gray-200 transition-all hover:ring-khamco-ribbon cursor-pointer"
        />
      );
    } else {
      return (
        <span className="flex items-center justify-center w-[2.75rem] h-[2.75rem] rounded-full bg-khamco-ribbon/10 text-khamco-ribbon ring-2 ring-transparent hover:ring-khamco-ribbon transition-all cursor-pointer">
          <User size={20} />
        </span>
      );
    }
  };

  return (
    <>
      <nav className="flex gap-[2rem] md:gap-[4rem] justify-between items-center py-3 px-4 md:px-8 sticky top-0 bg-white/95 backdrop-blur border-b border-gray-100 shadow-sm z-[1000]">
        <Link
          to="/home"
          className="text-[3rem] text-khamco-primary no-underline font-minimoBold shrink-0"
          title="Home"
        >
          {renderIcon(person)}
        </Link>
        {!menu && (
          <button
            className="text-khamco-ribbon cursor-pointer md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setMenu(!menu)}
            title="Open menu"
          >
            <Menu size={28} />
          </button>
        )}
        {menu && (
          <button
            className="text-khamco-ribbon cursor-pointer md:hidden absolute top-3 right-4 z-[1001] p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setMenu(!menu)}
            title="Close menu"
          >
            <X size={28} />
          </button>
        )}
        <div
          className={`md:flex flex-col md:flex-row flex-wrap justify-end list-none gap-3 md:gap-4 absolute md:relative w-full md:w-auto items-stretch md:items-center bg-white top-full left-0 z-50 py-4 px-4 md:p-0 border-b md:border-0 border-gray-100 shadow-lg md:shadow-none ${
            menu ? "flex" : "hidden"
          }`}
        >
          <div className="relative flex items-center w-full md:w-auto">
            <Globe
              size={16}
              className="pointer-events-none absolute left-3 rtl:left-auto rtl:right-3 text-gray-400"
            />
            <select
              value={i18n.resolvedLanguage}
              onChange={handleLanguageChange}
              className="appearance-none text-gray-700 bg-white border border-gray-200 py-2 pl-9 pr-4 rtl:pl-4 rtl:pr-9 rounded-lg cursor-pointer text-sm font-medium w-full md:w-auto text-center hover:border-khamco-ribbon focus:outline-none focus:ring-2 focus:ring-khamco-ribbon/30 transition-colors"
            >
              <option value="en">{t("English")}</option>
              <option value="hi">{t("Hindi")}</option>
              <option value="ur">{t("Urdu")}</option>
              <option value="ar">{t("Arabic")}</option>
              <option value="fr">{t("French")}</option>
              <option value="es">{t("Spanish")}</option>
              <option value="th">{t("Thai")}</option>
              <option value="vi">{t("Vietnamese")}</option>
              <option value="zh">{t("Chinese")}</option>
              <option value="it">{t("Italian")}</option>
            </select>
          </div>

          {!hasActiveSubscription(fullProfile) && (
            <Link
              to="/pricing"
              onClick={() => setMenu(false)}
              className="text-white text-center bg-gradient-to-r from-khamco-ribbon to-khamco-primary hover:opacity-90 py-2 px-4 rounded-lg cursor-pointer text-sm font-semibold w-full md:w-auto flex items-center justify-center gap-1.5 shadow-sm transition-opacity"
              title="Upgrade your plan"
            >
              <Sparkles size={16} />
              {t("Upgrade Plan")}
            </Link>
          )}

          <div
            className="relative flex md:justify-center justify-center pt-2 md:pt-0"
            ref={profileMenuRef}
          >
            <button
              onClick={() => setProfileMenuOpen((prev) => !prev)}
              title="Account menu"
            >
              {renderUserPhoto(person)}
            </button>
            {profileMenuOpen && (
              <div className="absolute right-0 rtl:right-auto rtl:left-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-[1001] text-left">
                <button
                  onClick={() => {
                    setProfileMenuOpen(false);
                    navigate(`/users/settings/${user.id}`);
                  }}
                  className="w-full flex items-center gap-2.5 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  <Settings size={17} />
                  {t("Profile Settings")}
                </button>
                <button
                  onClick={() => {
                    setProfileMenuOpen(false);
                    setShowLogoutConfirm(true);
                  }}
                  className="w-full flex items-center gap-2.5 px-4 py-3 text-[crimson] hover:bg-red-50 transition-colors border-t border-gray-100 text-sm font-medium"
                >
                  <PowerIcon size={17} />
                  {t("Logout")}
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
      {/* Logout decision dialog */}
      <Modal
        isOpen={showLogoutConfirm}
        setIsOpen={() => setShowLogoutConfirm(false)}
        type="small"
      >
        <div className="flex flex-col items-center text-center gap-5">
          <span className="flex items-center justify-center w-14 h-14 rounded-full bg-red-50 text-[crimson]">
            <AlertTriangle size={26} />
          </span>
          <div>
            <h2 className="text-xl font-minimoBold text-khamco-secondary">
              {t("Log Out")}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {t("Are you sure you want to log out?")}
            </p>
          </div>
          <div className="flex gap-3 w-full">
            <button
              onClick={() => setShowLogoutConfirm(false)}
              className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium cursor-pointer text-sm"
            >
              {t("Cancel")}
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 py-2.5 rounded-xl bg-[crimson] hover:bg-[red] text-white transition-colors font-medium cursor-pointer text-sm"
            >
              {t("Logout")}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Navbar;
